import * as React from 'react';
import { CalendarEvents, EventCard, GroupCard } from './event';
import { differenceInMinutes, format, set, setDate } from 'date-fns';
import { fromPairs, orderBy, times, uniq } from 'lodash';
import { EventGQLType } from 'core/gql/queries/calendar-events';

export type Filters = {
  divisions: string[];
  locations: string[];
  seasons: string[];
  sources: string[];
  teams: string[];
};

export const eventSources = {
  cl_scheduler: 'Scheduler',
  cl_season: 'Season',
  team: 'Team',
  tst: 'Tournament',
};

export const sortEvents = (events: EventGQLType[]) => {
  const sortedEvents = orderBy(
    events.map((x) => ({ ...x, startDate: new Date(x.startDateTime), endDate: new Date(x.endDateTime) })),
    ['startDate', 'endDate'],
    ['asc', 'desc']
  );

  return sortedEvents;
};

export const groupEvents = (events: EventGQLType[]) => {
  const sortedEvents = sortEvents(events);

  const allGroupedEvents = sortedEvents.reduce((prev, event) => {
    const date = new Intl.DateTimeFormat('en-US').format(event.startDate);
    const currentDay = prev[date] || { groups: [], individualEvents: [] };
    const matchingGroup = currentDay.groups.find(
      (x) =>
        (event.startDate >= x.startsAt && event.startDate < x.endsAt) ||
        (event.endDate > x.startsAt && event.endDate < x.endsAt)
    );

    const { startsAt = event.startDate, endsAt = event.endDate, events: matchingEvents = [] } = matchingGroup || {};
    const updatedGroup = {
      startsAt: startsAt > event.startDate ? event.startDate : startsAt,
      endsAt: endsAt > event.endDate ? endsAt : event.endDate,
      events: [...matchingEvents, event],
    };

    return {
      ...prev,
      [date]: !matchingGroup
        ? { individualEvents: [], groups: [...currentDay.groups, updatedGroup] }
        : { individualEvents: [], groups: currentDay.groups.map((x) => (x.startsAt === startsAt ? updatedGroup : x)) },
    } as CalendarEvents;
  }, {} as CalendarEvents);

  const groupsAndIndividualEvents = Object.entries(allGroupedEvents).map(([key, value]) => {
    const groups = value.groups.filter((x) => x.events.length > 2);
    const smallGroups = value.groups.filter((x) => x.events.length < 3);
    const individualEvents = smallGroups.flatMap((x) =>
      x.events.map((event, i) => ({
        ...event,
        position: x.events.length === 1 ? 'centralized' : i === 0 ? 'left' : 'right',
      }))
    );

    return [key, { groups, individualEvents }];
  });

  return fromPairs(groupsAndIndividualEvents) as CalendarEvents;
};

const matchHourBlock = (date: Date, startDate: Date) =>
  startDate.getHours() === date.getHours() &&
  startDate.getMinutes() >= date.getMinutes() &&
  startDate.getMinutes() < date.getMinutes() + 30;

export const getEventCards =
  (
    CalendarEvents: CalendarEvents,
    selectEvent: (x: any) => void,
    selectGroup: (x: any) => void,
    GA4data: { user: string; orgId: string }
  ) =>
  (date: Date) => {
    const eventsByDay = CalendarEvents[new Intl.DateTimeFormat('en-US').format(date)];
    if (!eventsByDay) return [];

    const matchingGroup = eventsByDay.groups.find(({ startsAt }) => matchHourBlock(date, startsAt));

    if (matchingGroup) return <GroupCard onClick={() => selectGroup(matchingGroup)} {...matchingGroup} />;

    const { individualEvents } = eventsByDay;

    const matchingEvents = individualEvents.filter(({ startDate }) => matchHourBlock(date, startDate));
    const calendarBottom = set(date, { date: date.getDate() + 1, hours: 0, minutes: 0, seconds: 0 });
    const maxHeight = Math.round(differenceInMinutes(calendarBottom, date) / 15) * 25;

    return matchingEvents.map((event) => (
      <EventCard
        GA4data={GA4data}
        onClick={() => selectEvent(event)}
        marginLeft={event.position === 'right' && matchingEvents.length === 1 ? '50%' : 'unset'}
        maxHeight={maxHeight}
        {...event}
      />
    ));
  };

export const getWeek = (monday: Date) => [
  monday,
  ...times(6, Number).map((index) => setDate(monday, monday.getDate() + index + 1)),
];

export const getMonthHeader = (week: Date[]) => {
  const sameYear = week[0].getFullYear() === week[6].getFullYear();
  const months = uniq([format(week[0], 'MMM'), format(week[6], 'MMM')]);

  return sameYear
    ? `${months.join(' - ')} ${week[0].getFullYear()}`
    : `${months[0]} ${week[0].getFullYear()} - ${months[1]} ${week[6].getFullYear()}`;
};

export const getFormatedEventDateString = (date: Date): string => {
  const formatOptions = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };

  return new Intl.DateTimeFormat('en-US', formatOptions as any).format(date);
};

export const getExportFormattedEventDateString = (date: Date): string => {
  const formatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

  return new Intl.DateTimeFormat('en-US', formatOptions as any).format(date);
};

export const getExportFormattedEventTimeString = (date: Date): string => {
  const formatOptions = { timeStyle: 'long' };

  return new Intl.DateTimeFormat('en-US', formatOptions as any).format(date);
};

